import { Bookmark } from 'lucide-react'
import { EmptyState } from '../../components/ui/empty-state'
import { createFileRoute, Link } from '@tanstack/react-router'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { myChannelsQuery } from '../../lib/channels'
import { usersQuery } from '../../lib/users'
import { Avatar } from '../../components/avatar'
import { Markdown } from '../../components/messages/markdown'
import { useToggleSave } from '../../components/messages/bookmarks'

// DIS-50: the caller's saved messages, newest save first.

export const savedQuery = queryOptions({
  queryKey: ['saved'],
  queryFn: async () => {
    const { data, response } = await api.GET('/me/saved')
    if (!data) throw new Error(`GET saved failed: ${response.status}`)
    return data.saved
  },
})

export const Route = createFileRoute('/_app/saved')({
  component: SavedPage,
})

function SavedPage() {
  const saved = useQuery(savedQuery)
  const users = useQuery(usersQuery)
  const channels = useQuery(myChannelsQuery)
  const byId = new Map((users.data ?? []).map((u) => [u.id, u]))
  const chan = new Map((channels.data ?? []).map((c) => [c.id, c]))
  return (
    <>
      <header className="flex h-12 items-center gap-3 border-b border-border px-4" data-testid="channel-header">
        <h1 className="font-semibold">Saved</h1>
        <span className="text-sm text-fg-muted">Only you can see this list.</span>
      </header>
      <div className="flex-1 overflow-y-auto p-4" data-testid="saved-list">
        {saved.data?.length === 0 && <EmptyState icon={<Bookmark size={16} />} title="Nothing saved" hint="Use “Save” in a message's menu to keep it here — a private list only you can see." />}
        <ul className="mx-auto max-w-3xl space-y-3">
          {saved.data?.map((s) => {
            const author = s.message.author_id ? byId.get(s.message.author_id) : undefined
            const c = chan.get(s.message.channel_id)
            return <SavedRow key={s.message.id} id={s.message.id} channelId={s.message.channel_id} body={s.message.body} author={author} channel={c} savedAt={s.saved_at} />
          })}
        </ul>
      </div>
    </>
  )
}

function SavedRow({ id, channelId, body, author, channel, savedAt }: { id: string; channelId: string; body: string; author?: { display_name: string; handle: string; avatar_url?: string }; channel?: { slug?: string; name?: string; id: string }; savedAt: string }) {
  const toggle = useToggleSave(channelId)
  return (
    <li className="group rounded-md border border-border p-3 text-sm" data-testid="saved-message">
      <div className="mb-1 flex items-center gap-2 text-xs text-fg-muted">
        {author && <Avatar user={author} size={18} />}
        <span className="font-medium text-fg">{author?.display_name ?? 'Unknown'}</span>
        {channel && (
          <Link to="/c/$slug/$messageId" params={{ slug: channel.slug ?? channel.id, messageId: id }} className="hover:text-fg">
            in #{channel.name}
          </Link>
        )}
        <span className="ml-auto" title={new Date(savedAt).toLocaleString()}>
          saved {new Date(savedAt).toLocaleDateString()}
        </span>
        <button type="button" className="hidden text-danger group-hover:block" onClick={() => toggle.mutate({ id, on: false })} aria-label="Remove from saved">
          Remove
        </button>
      </div>
      <Markdown source={body} />
    </li>
  )
}
